<script setup lang="ts">
import { computed, ref } from 'vue';
import { useTemplateRef } from 'vue';
import { capitalizeStr } from '@Helpers';

const props = defineProps<{
  field: any;
  rowData: any;
  settings: any;
}>();

const fieldElement = useTemplateRef<HTMLParagraphElement>('fieldElement');

const tooltipValue = ref<string | null>(null);

const badgeValue = computed(() => {
  const badgeConfig = props.settings?.render?.badge;
  const missingParams = !badgeConfig?.targetKey || !Array.isArray(badgeConfig.targetKey);
  if (missingParams) return null;

  // NOTE :: targetKey is always an array representing the path to the value
  let value = props.rowData;
  for (const key of badgeConfig.targetKey) {
    if (value == null) return null;
    value = value[key];
  }

  return value;
});

const badgeLabel = computed(() => {
  const badgeConfig = props.settings?.render?.badge;
  if (!badgeValue.value) return null;

  // NOTE :: Format badge label with unit/plural if configured
  if (typeof badgeValue.value === 'number' && badgeValue.value > 0) {
    const unit = badgeConfig?.unit;
    if (unit) {
      const plural = badgeConfig?.plural || `${unit}s`;
      return `${badgeValue.value} ${badgeValue.value === 1 ? unit : plural}`;
    }
  }

  return badgeValue.value;
});

function onMouseEnter() {
  if (!fieldElement.value) return;

  const isTruncated = fieldElement.value.scrollWidth > fieldElement.value.clientWidth;

  tooltipValue.value = isTruncated ? props.field : null;
}
</script>

<template>
  <div
    class="min-w-24"
    @mouseenter="onMouseEnter"
  >
    <span
      v-if="rowData.label"
      class="absolute left-1 top-0.5 text-sm font-semibold text-gray-600"
    >
      {{ capitalizeStr(rowData.label) }}
    </span>

    <div class="flex items-center gap-2">
      <p
        v-tooltip.left="tooltipValue"
        class="overflow-hidden text-ellipsis"
        ref="fieldElement"
      >
        {{ field }}
      </p>

      <span
        v-if="badgeLabel"
        :class="
          settings?.render?.badge?.className ||
          'flex-shrink-0 rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800'
        "
      >
        {{ badgeLabel }}
      </span>
    </div>
  </div>
</template>
